import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { workspaceApi } from '@/lib/api'
import { Skeleton } from '@/components/ui/skeleton'
import { EmptyState } from '@/components/ui/empty-state'
import type { WorkspaceMemberRole } from '@/types/workspace'

const ROLE_LABEL: Record<WorkspaceMemberRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
  viewer: 'Viewer',
}

const ROLE_COLOR: Record<WorkspaceMemberRole, string> = {
  owner: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400',
  admin: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
  member: 'bg-green-500/10 text-green-600 dark:text-green-400',
  viewer: 'bg-secondary text-muted-foreground',
}

export default function WorkspaceMembersPage() {
  const { slug } = useParams<{ slug: string }>()

  const { data, isLoading, error } = useQuery({
    queryKey: ['workspace', slug, 'members'],
    queryFn: () => workspaceApi.getMembers(slug!),
    enabled: !!slug,
  })

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Member</h1>
          <p className="text-sm text-muted-foreground">Kelola anggota workspace.</p>
        </div>
        {/* TODO: Add invite button */}
      </div>

      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-14" />
          ))}
        </div>
      )}

      {error && (
        <p className="text-sm text-destructive">Gagal memuat member. Coba refresh halaman.</p>
      )}

      {!isLoading && !error && data && (
        <div className="rounded-xl border border-border bg-card">
          {data.length === 0 ? (
            <div className="p-8">
              <EmptyState
                title="Belum ada member."
                description="Invite anggota untuk berkolaborasi di workspace ini."
              />
            </div>
          ) : (
            <ul className="divide-y">
              {data.map((member) => (
                <li key={member.id} className="flex items-center justify-between px-4 py-3">
                  <div>
                    <p className="text-sm font-medium">
                      {member.display_name ?? member.email ?? member.user_id}
                    </p>
                    {member.display_name && member.email && (
                      <p className="text-xs text-muted-foreground">{member.email}</p>
                    )}
                  </div>
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${ROLE_COLOR[member.role]}`}
                  >
                    {ROLE_LABEL[member.role]}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
